import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FindClientOrProperty } from '@/components/FindClientOrProperty';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { propertyLabel } from '@/utils/createAppraisal';
import { toast } from 'sonner';
import {
  Loader2, ClipboardCheck, ArrowLeft, Home, MapPin, Ruler, Send,
  MessageSquareWarning,
} from 'lucide-react';

/**
 * Free Appraisal Form — one-screen manual entry.
 *
 * Sprint 2 (2026-09-14). Two entry modes:
 *   · Cold entry: `/free-appraisal` — blank form.
 *   · Web-lead promotion: `/free-appraisal/:leadId` — pre-fills from
 *     the lead, and on submit flips the lead to `status="promoted"`.
 *
 * Appraisals created here are stamped `appraisal_type="free_estimate"`,
 * so the Detailed Appraisal on-site inspection fields intentionally
 * stay blank — those get filled during the roof inspection.
 */

const QUOTE_TYPE_CHOICES = [
  'Reroof', 'Restoration', 'Repairs', 'Guttering',
  'Painting', 'Cleaning', 'Roof Inspection', 'Other',
];

function Section({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-3"
    >
      <header className="flex items-start gap-2 border-b border-slate-100 pb-2">
        <Icon size={18} className="text-sky-600 mt-0.5" />
        <div>
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-0.5">{hint}</span>}
    </label>
  );
}

const inputCls =
  'mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 disabled:bg-slate-100 disabled:text-slate-600';

const NUM = (v) => (v === '' || v === null || v === undefined ? '' : Number(v));

export default function FreeAppraisalFormPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();
  const qClient = searchParams.get('client_id') || '';
  const qProp = searchParams.get('property_id') || '';
  const qFrom = searchParams.get('from_appraisal_id') || '';
  const needsFind = !leadId && !qClient && !qProp && searchParams.get('new') !== '1';
  const [ctxClient, setCtxClient] = useState(null);
  const [ctxProps, setCtxProps] = useState([]);
  const [selPropId, setSelPropId] = useState('');
  const [newPropChosen, setNewPropChosen] = useState(false);
  const [loadingCtx, setLoadingCtx] = useState(!!(qClient || qProp));

  const [loadingLead, setLoadingLead] = useState(!!leadId);
  const [lead,        setLead]        = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [correcting,  setCorrecting]  = useState(false);
  const [correctionNote, setCorrectionNote] = useState('');

  const [client, setClient] = useState({
    name_first: '', name_last: '', email: '', mobile: '',
  });
  const [prop, setProp] = useState({
    property_address_num: '', property_address_street: '',
    property_address_suburb: '', property_address_city: '',
    property_address_postcode: '', property_type: 'Residential',
  });
  const [roof, setRoof] = useState({
    description: 'Main Roof', roof_type: '', roof_colour: '',
    roof_age: '', roof_brand: '', roof_pitch: '', roof_profile: '',
    roof_stories: 1, roof_m2: 0, floor_m2: 0,
    ridge_length: 0, perimeter_length: 0, barge_length: 0,
    valley_length: 0, roof_angle: 0,
  });
  const [quoteTypes, setQuoteTypes] = useState([]);
  const [comments,   setComments]   = useState('');
  const [urgency,    setUrgency]    = useState('');
  const [contactMethod, setContactMethod] = useState('phone');

  // Load lead pre-fill.
  useEffect(() => {
    if (!leadId) { setLoadingLead(false); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/web-leads/${leadId}`);
        if (!alive) return;
        setLead(data);
        // Split "First Last".
        const nameBits = (data.name || '').trim().split(' ');
        setClient({
          name_first: nameBits[0] || '',
          name_last:  nameBits.slice(1).join(' ') || '',
          email:      data.email || '',
          mobile:     data.phone || '',
        });
        // Address — the intake form sends a flat `address` string plus
        // pre-parsed suburb / city / postcode when the designer added them.
        const addrBits = (data.address || '').split(',').map((s) => s.trim());
        setProp((p) => ({
          ...p,
          property_address_street:  addrBits[0] || '',
          property_address_suburb:  data.suburb  || addrBits[1] || '',
          property_address_city:    data.city    || addrBits[2] || '',
          property_address_postcode:data.postcode || '',
          property_type: data.property_type || 'Residential',
        }));
        setRoof((r) => ({
          ...r,
          roof_type:    data.roof_type   || '',
          roof_age:     data.roof_age    || '',
          roof_stories: Number(data.roof_stories) || 1,
        }));
        setQuoteTypes(data.preferred_quote_types || []);
        setComments(data.message || '');
        setUrgency(data.how_soon || '');
        setContactMethod(data.preferred_contact_method || 'phone');
      } catch (e) {
        toast.error('Could not load web lead');
      } finally {
        if (alive) setLoadingLead(false);
      }
    })();
    return () => { alive = false; };
  }, [leadId, api]);

  // Existing client / property context — reuse records, never duplicate.
  useEffect(() => {
    if (!qClient && !qProp) return;
    (async () => {
      try {
        if (qProp) {
          const { data } = await api.get(`/properties/${qProp}`);
          setCtxClient(data.client || { _id: data.client_id });
          setCtxProps([data]);
          setSelPropId(qProp);
        } else {
          const { data } = await api.get(`/clients/${qClient}`);
          setCtxClient(data);
          const props = data.properties || [];
          setCtxProps(props);
          if (props.length === 1) setSelPropId(props[0]._id);
        }
      } catch { toast.error('Could not load client / property'); }
      setLoadingCtx(false);
    })();
  }, [qClient, qProp, api]);

  useEffect(() => {
    if (!ctxClient) return;
    setClient({ name_first: ctxClient.name_first || '', name_last: ctxClient.name_last || '', email: ctxClient.email || '', mobile: ctxClient.phone_mobile || '' });
  }, [ctxClient]);

  useEffect(() => {
    if (!selPropId) return;
    api.get(`/properties/${selPropId}`).then(({ data }) => {
      setProp(p => ({ ...p,
        property_address_num: data.property_address_num || '', property_address_street: data.property_address_street || '',
        property_address_suburb: data.property_address_suburb || '', property_address_city: data.property_address_city || '',
        property_address_postcode: data.property_area_code || data.property_address_postcode || '' }));
      const rd = (data.roof_details || [])[0];
      if (rd) setRoof(r => ({ ...r, description: rd.description || r.description, roof_type: rd.roof_type || '', roof_colour: rd.roof_colour || '', roof_age: rd.roof_age || '', roof_brand: rd.roof_brand || '', roof_pitch: rd.roof_pitch || '' }));
    }).catch(() => {});
  }, [selPropId, api]);

  const clientLocked = !!ctxClient;
  const propLocked = !!selPropId;
  const awaitingPropChoice = clientLocked && ctxProps.length > 1 && !selPropId && !newPropChosen;

  const canSubmit = useMemo(() => {
    if (!clientLocked && !client.name_first && !client.name_last) return false;
    if (clientLocked && ctxProps.length > 1 && !propLocked && !newPropChosen) return false;
    if (!propLocked && !prop.property_address_street) return false;
    return true;
  }, [client, prop, clientLocked, propLocked, ctxProps.length, newPropChosen]);

  const choosePropOption = (v) => {
    if (v === '__new') {
      setSelPropId(''); setNewPropChosen(true);
      setProp(p => ({ ...p, property_address_num: '', property_address_street: '', property_address_suburb: '', property_address_city: '', property_address_postcode: '' }));
      setRoof(r => ({ ...r, description: 'Main Roof', roof_type: '', roof_colour: '', roof_age: '', roof_brand: '', roof_pitch: '' }));
    } else { setNewPropChosen(false); setSelPropId(v); }
  };

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Client name + street address are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        from_web_lead_id: leadId || null,
        client,
        property: prop,
        roof_details: [{
          ...roof,
          roof_stories: NUM(roof.roof_stories) || 1,
          roof_m2:      NUM(roof.roof_m2),
          floor_m2:     NUM(roof.floor_m2),
          ridge_length: NUM(roof.ridge_length),
          perimeter_length: NUM(roof.perimeter_length),
          barge_length: NUM(roof.barge_length),
          valley_length: NUM(roof.valley_length),
          roof_angle:    NUM(roof.roof_angle),
        }],
        preferred_quote_types: quoteTypes,
        appraisal_comments: comments,
        urgency,
        preferred_contact_method: contactMethod,
        existing_client_id: ctxClient?._id || null,
        existing_property_id: selPropId || null,
        source_appraisal_id: qFrom || null,
      };
      let data;
      try {
        ({ data } = await api.post('/free-appraisal', payload));
      } catch (err) {
        const r = err?.response?.data;
        if (r?.code !== 'duplicate_appraisal') throw err;
        if (!window.confirm(`${r.detail}\n\nCreate another one anyway?`)) return;
        ({ data } = await api.post('/free-appraisal', payload, { params: { confirm_duplicate: true } }));
      }
      toast.success(data.already_promoted ? 'Lead already promoted — opening appraisal' : leadId ? 'Free appraisal created · lead promoted' : 'Free appraisal created');
      navigate(`/appraisals/${data.appraisal_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.data?.code !== 'duplicate_address') toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const requestCorrection = async () => {
    if (!leadId) return;
    setCorrecting(true);
    try {
      const { data } = await api.post(
        `/web-leads/${leadId}/request-address-correction`,
        { note: correctionNote || '' },
      );
      toast.success(data.email_dispatched
        ? `Correction request emailed to ${data.email_target}`
        : `Correction logged (email queue is mocked — target: ${data.email_target || 'unknown'})`
      );
      navigate('/web-leads');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log correction request');
    } finally {
      setCorrecting(false);
    }
  };

  if (needsFind) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="free-appraisal-find-page">
        <div className="max-w-xl mx-auto p-6 space-y-5">
          <button data-testid="free-appraisal-back" onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-700 text-xs inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Back
          </button>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <ClipboardCheck className="text-sky-600" /> New Free Appraisal
          </h1>
          <p className="text-sm text-slate-500">Who is this for? Search first — existing clients and properties are reused so nothing is duplicated.</p>
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <FindClientOrProperty mode="free" />
          </div>
        </div>
      </div>
    );
  }

  if (loadingLead || loadingCtx) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-sky-600" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-4xl mx-auto p-6 space-y-5">
        <header className="flex items-start justify-between gap-3">
          <div>
            <button
              data-testid="free-appraisal-back"
              onClick={() => (leadId ? navigate('/web-leads') : navigate(-1))}
              className="text-slate-500 hover:text-slate-700 text-xs inline-flex items-center gap-1"
            >
              <ArrowLeft size={12} /> {leadId ? 'Back to Web Leads' : 'Back'}
            </button>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 mt-1 inline-flex items-center gap-2">
              <ClipboardCheck className="text-sky-600" /> New Free Appraisal
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {leadId ? (
                <>Promoting web lead <span className="font-mono text-slate-700">{lead?.lead_no || leadId}</span> — details pre-filled below.</>
              ) : clientLocked ? (
                <>For existing client <span className="font-semibold text-slate-700" data-testid="free-appraisal-ctx-client">{`${ctxClient.name_first || ''} ${ctxClient.name_last || ''}`.trim() || 'Client'}</span>{propLocked ? <> at <span className="font-semibold text-slate-700" data-testid="free-appraisal-ctx-property">{propertyLabel(ctxProps.find(p => p._id === selPropId))}</span></> : ''} — no new client{propLocked ? ' or property' : ''} will be created.</>
              ) : (
                <>Manual entry — phone in or walk-in customer. Roof measurements come from Google Earth; on-site details captured later during inspection.</>
              )}
            </p>
          </div>
        </header>

        {/* Client */}
        <Section icon={Home} title="Client" testid="free-appraisal-client-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="First name">
              <input data-testid="free-appraisal-client-first" disabled={clientLocked} className={inputCls}
                value={client.name_first}
                onChange={(e) => setClient({ ...client, name_first: e.target.value })}
              />
            </Field>
            <Field label="Last name">
              <input data-testid="free-appraisal-client-last" disabled={clientLocked} className={inputCls}
                value={client.name_last}
                onChange={(e) => setClient({ ...client, name_last: e.target.value })}
              />
            </Field>
            <Field label="Email">
              <input data-testid="free-appraisal-client-email" disabled={clientLocked} className={inputCls} type="email"
                value={client.email}
                onChange={(e) => setClient({ ...client, email: e.target.value })}
              />
            </Field>
            <Field label="Mobile">
              <input data-testid="free-appraisal-client-mobile" disabled={clientLocked} className={inputCls}
                value={client.mobile}
                onChange={(e) => setClient({ ...client, mobile: e.target.value })}
              />
            </Field>
          </div>
        </Section>

        {/* Property */}
        <Section icon={MapPin} title="Property Address"
          subtitle={awaitingPropChoice ? 'Choose a property above.' : propLocked ? 'Existing property on file — address is locked.' : clientLocked ? 'New property for this client — duplicate addresses are blocked.' : 'Edit if the lead-supplied address needs correcting.'}
          testid="free-appraisal-property-section"
        >
          {clientLocked && ctxProps.length > 1 && (
            <div>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which property? *</span>
              <Select value={selPropId || (newPropChosen ? '__new' : '')} onValueChange={choosePropOption}>
                <SelectTrigger className="mt-1" data-testid="free-appraisal-property-picker"><SelectValue placeholder="Choose a property…" /></SelectTrigger>
                <SelectContent>
                  {ctxProps.map(p => <SelectItem key={p._id} value={p._id} data-testid={`free-appraisal-property-option-${p._id}`}>{propertyLabel(p)}</SelectItem>)}
                  <SelectItem value="__new" data-testid="free-appraisal-property-option-new">+ New property for this client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-1">
              <Field label="Number">
                <input data-testid="free-appraisal-prop-num" disabled={propLocked || awaitingPropChoice} className={inputCls}
                  value={prop.property_address_num}
                  onChange={(e) => setProp({ ...prop, property_address_num: e.target.value })}
                />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Street">
                <input data-testid="free-appraisal-prop-street" disabled={propLocked || awaitingPropChoice} className={inputCls}
                  value={prop.property_address_street}
                  onChange={(e) => setProp({ ...prop, property_address_street: e.target.value })}
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Suburb">
                <input data-testid="free-appraisal-prop-suburb" disabled={propLocked || awaitingPropChoice} className={inputCls}
                  value={prop.property_address_suburb}
                  onChange={(e) => setProp({ ...prop, property_address_suburb: e.target.value })}
                />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="City">
                <input data-testid="free-appraisal-prop-city" disabled={propLocked || awaitingPropChoice} className={inputCls}
                  value={prop.property_address_city}
                  onChange={(e) => setProp({ ...prop, property_address_city: e.target.value })}
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Postcode">
                <input data-testid="free-appraisal-prop-postcode" disabled={propLocked || awaitingPropChoice} className={inputCls}
                  value={prop.property_address_postcode}
                  onChange={(e) => setProp({ ...prop, property_address_postcode: e.target.value })}
                />
              </Field>
            </div>
            <div className="md:col-span-1">
              <Field label="Type">
                <select data-testid="free-appraisal-prop-type" disabled={propLocked || awaitingPropChoice} className={inputCls}
                  value={prop.property_type}
                  onChange={(e) => setProp({ ...prop, property_type: e.target.value })}
                >
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Rural</option>
                  <option>Body Corporate</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Address correction ask — only shows when we have a lead ref. */}
          {leadId && (
            <div className="mt-3 border-t border-slate-100 pt-3 space-y-2 bg-amber-50/40 rounded-md p-3 border border-amber-200/60">
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
                <MessageSquareWarning size={13} /> Address unclear?
              </div>
              <p className="text-[11px] text-amber-800/80">
                Send a note back to the lead asking them to correct the address.
                Lead status flips to <em>awaiting_address_correction</em> so it doesn't
                get promoted by mistake. Email dispatch is currently <b>mocked</b>.
              </p>
              <textarea data-testid="free-appraisal-correction-note" rows={2}
                className={inputCls}
                placeholder="e.g. Please confirm the street number and postcode — the current entry is missing detail."
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
              />
              <button data-testid="free-appraisal-correction-send"
                onClick={requestCorrection}
                disabled={correcting}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1"
              >
                {correcting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send correction request
              </button>
            </div>
          )}
        </Section>

        {/* Roof details — Google Earth measurements */}
        <Section icon={Ruler} title="Roof Details (Google Earth)"
          subtitle="Measurements gathered from Google Earth. On-site inspection details are captured later on the appraisal detail page."
          testid="free-appraisal-roof-section"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Roof type">
              <input data-testid="free-appraisal-roof-type" className={inputCls}
                value={roof.roof_type}
                onChange={(e) => setRoof({ ...roof, roof_type: e.target.value })}
              />
            </Field>
            <Field label="Colour">
              <input data-testid="free-appraisal-roof-colour" className={inputCls}
                value={roof.roof_colour}
                onChange={(e) => setRoof({ ...roof, roof_colour: e.target.value })}
              />
            </Field>
            <Field label="Age">
              <input data-testid="free-appraisal-roof-age" className={inputCls}
                value={roof.roof_age}
                onChange={(e) => setRoof({ ...roof, roof_age: e.target.value })}
                placeholder="e.g. 20+ years"
              />
            </Field>
            <Field label="Storeys">
              <input data-testid="free-appraisal-roof-stories" className={inputCls} type="number" min={1}
                value={roof.roof_stories}
                onChange={(e) => setRoof({ ...roof, roof_stories: Number(e.target.value) })}
              />
            </Field>
            <Field label="Roof area (m²)" hint="Google Earth: measure the roof polygon.">
              <input data-testid="free-appraisal-roof-m2" className={inputCls} type="number" min={0} step="0.1"
                value={roof.roof_m2}
                onChange={(e) => setRoof({ ...roof, roof_m2: e.target.value })}
              />
            </Field>
            <Field label="Floor area (m²)">
              <input data-testid="free-appraisal-floor-m2" className={inputCls} type="number" min={0} step="0.1"
                value={roof.floor_m2}
                onChange={(e) => setRoof({ ...roof, floor_m2: e.target.value })}
              />
            </Field>
            <Field label="Roof pitch">
              <input data-testid="free-appraisal-roof-pitch" className={inputCls}
                value={roof.roof_pitch}
                onChange={(e) => setRoof({ ...roof, roof_pitch: e.target.value })}
                placeholder="e.g. 25°"
              />
            </Field>
            <Field label="Roof angle (°)">
              <input data-testid="free-appraisal-roof-angle" className={inputCls} type="number" min={0} step="0.1"
                value={roof.roof_angle}
                onChange={(e) => setRoof({ ...roof, roof_angle: e.target.value })}
              />
            </Field>
            <Field label="Ridge length (m)">
              <input data-testid="free-appraisal-ridge-length" className={inputCls} type="number" min={0} step="0.1"
                value={roof.ridge_length}
                onChange={(e) => setRoof({ ...roof, ridge_length: e.target.value })}
              />
            </Field>
            <Field label="Perimeter (m)">
              <input data-testid="free-appraisal-perimeter-length" className={inputCls} type="number" min={0} step="0.1"
                value={roof.perimeter_length}
                onChange={(e) => setRoof({ ...roof, perimeter_length: e.target.value })}
              />
            </Field>
            <Field label="Barge length (m)">
              <input data-testid="free-appraisal-barge-length" className={inputCls} type="number" min={0} step="0.1"
                value={roof.barge_length}
                onChange={(e) => setRoof({ ...roof, barge_length: e.target.value })}
              />
            </Field>
            <Field label="Valley length (m)">
              <input data-testid="free-appraisal-valley-length" className={inputCls} type="number" min={0} step="0.1"
                value={roof.valley_length}
                onChange={(e) => setRoof({ ...roof, valley_length: e.target.value })}
              />
            </Field>
          </div>
        </Section>

        {/* Job scoping */}
        <Section icon={ClipboardCheck} title="Requested Work"
          subtitle="What the customer asked about + any additional context." testid="free-appraisal-scope-section"
        >
          <div className="space-y-3">
            <div>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Preferred quote types</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {QUOTE_TYPE_CHOICES.map((qt) => {
                  const on = quoteTypes.includes(qt);
                  return (
                    <button
                      key={qt}
                      data-testid={`free-appraisal-quote-chip-${qt.toLowerCase().replace(/\s+/g, '-')}`}
                      type="button"
                      onClick={() => setQuoteTypes(on ? quoteTypes.filter((x) => x !== qt) : [...quoteTypes, qt])}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-300 hover:border-sky-400'}`}
                    >
                      {qt}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Urgency">
                <select data-testid="free-appraisal-urgency" className={inputCls}
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value)}
                >
                  <option value="">—</option>
                  <option>ASAP</option>
                  <option>Within 1 month</option>
                  <option>Within 3 months</option>
                  <option>No rush / planning</option>
                </select>
              </Field>
              <Field label="Preferred contact method">
                <select data-testid="free-appraisal-contact" className={inputCls}
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                >
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </Field>
            </div>
            <Field label="Notes / customer message">
              <textarea data-testid="free-appraisal-comments" className={inputCls} rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Include leaks reported, access notes, contact preferences etc."
              />
            </Field>
          </div>
        </Section>

        {/* Submit bar */}
        <div className="sticky bottom-4 bg-white border border-slate-200 rounded-lg p-3 shadow-lg flex items-center justify-between gap-3 z-10">
          <div className="text-xs text-slate-500">
            {leadId ? 'Submitting will promote the lead + open the new appraisal.'
              : awaitingPropChoice ? 'Choose a property above to continue.'
              : clientLocked ? `Submitting will add the free appraisal to this client${propLocked ? ' + property' : ' and a new property'}.`
              : 'Submitting will create client + property + appraisal and open it.'}
          </div>
          <button
            data-testid="free-appraisal-submit"
            onClick={submit}
            disabled={!canSubmit || saving}
            className="bg-sky-600 hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-md px-5 py-2 inline-flex items-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCheck size={16} />}
            Create Free Appraisal
          </button>
        </div>
      </div>
    </div>
  );
}
